/**
 * UserInstrumentsTab.tsx
 * User's personal instrument library tab component
 *
 * Features:
 * - AGGrid table with internal scrolling (fills available space)
 * - HeroUI Drawer for search, filters, and view presets
 * - Toolbar: View presets + Filter (left), Selection + Actions (right)
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardHeader, CardBody, CardFooter, Button, Drawer, DrawerContent, DrawerHeader, DrawerBody, Input, Select, SelectItem, Chip, ButtonGroup, useDisclosure, useToastNotification, Popover, PopoverTrigger, PopoverContent } from '@enfomentis/ui';
import { AGGridTable } from '@enfomentis/ui';
import { Filter, Plus, Search, Eye, EyeOff, Edit, Copy, Trash2, Archive, RotateCcw, Package } from 'lucide-react';
import { InstrumentModal } from './InstrumentModal';
import { InstrumentImportModal } from './InstrumentImportModal';
import { TemplateReviewModal } from './TemplateReviewModal';
import type { ColDef, GridApi } from 'ag-grid-community';
import type { Instrument } from '@/types/schema/Instrument';
import type { UserInstrumentsState } from './types';
import { instrumentService } from '@/services/InstrumentService';
import { instrumentUsageService } from '@/services/InstrumentUsageService';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { InstrumentActionSheet } from './InstrumentActionSheet';
import type { InstrumentUsageResult } from '@/types/transient/InstrumentUsage';
import { getPresetOptions, applyPreset, detectCurrentPreset, type PresetKey } from './columnPresets';
import { capitalizeText, handleFilterSelection, formatDate, applyClassificationFilters } from './utils';
import { useIsDesktop } from '@/hooks';


const StatusRenderer: React.FC<{ data: Instrument }> = ({ data }) => {
  return (
    <Chip 
      color={data.status === 'active' ? 'success' : 'default'} 
      size="sm"
      variant="flat"
    >
      {data.status === 'active' ? 'Active' : 'Inactive'}
    </Chip>
  );
};


/**
 * Main UserInstrumentsTab component
 *
 * Standalone instruments view (no longer a tab within InstrumentExplorerTabs).
 * Manages its own filter/column state internally.
 */
export const UserInstrumentsTab: React.FC = () => {
  // Internal state for filters and column visibility
  // Note: These defaults are lifted from InstrumentExplorerTabs to preserve existing behavior
  const [state, setState] = useState<UserInstrumentsState>({
    searchTerm: '',
    assetClassFilter: new Set(['all']),
    marketSegmentFilter: new Set(['all']),
    subAssetClassFilter: new Set(['all']),
    statusFilter: new Set(['active', 'inactive']),
    templateStatusFilter: new Set(['all']),
    sourceFilter: new Set(['all']),
    // Default to 'general' preset columns for cleaner initial view
    visibleColumns: new Set(['symbol', 'displayName', 'assetClass', 'marketSegment', 'subAssetClass', 'status', 'quoteCurrency']),
    selectedPreset: 'general',
    isDrawerOpen: false
  });
  // Mobile/desktop detection
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;

  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingInstrument, setEditingInstrument] = useState<Instrument | undefined>(undefined);
  const [viewingInstrument, setViewingInstrument] = useState<Instrument | undefined>(undefined);
  const [duplicatingInstrument, setDuplicatingInstrument] = useState<Instrument | undefined>(undefined);
  const [selectedInstruments, setSelectedInstruments] = useState<Set<string>>(new Set());
  const [gridApi, setGridApi] = useState<GridApi<Instrument> | null>(null);
  
  // Modal state management
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isTemplateReviewModalOpen, onClose: onTemplateReviewModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const { isOpen: isActionSheetOpen, onOpen: onActionSheetOpen, onClose: onActionSheetClose } = useDisclosure();
  const { isOpen: isImportModalOpen, onOpen: onImportModalOpen, onClose: onImportModalClose } = useDisclosure();

  // Popover state for Add button
  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false);

  // Archive visibility toggle (separate from preset system)
  const [showArchived, setShowArchived] = useState(false);

  // Track if an action was just performed (for auto-return to default view)
  const justPerformedActionRef = useRef(false);

  // Toast notifications
  const { addToast } = useToastNotification();

  // Delete modal state
  const [deleteCandidates, setDeleteCandidates] = useState<InstrumentUsageResult[]>([]);
  const [isLoadingUsage, setIsLoadingUsage] = useState<boolean>(false);
  const [isProcessingDelete, setIsProcessingDelete] = useState<boolean>(false);

  // Calculate outdated instruments and count for mobile badge and drawer button
  const outdatedInstruments = useMemo(() => {
    return instruments.filter(instrument => {
      const status = instrumentService.getTemplateStatus(instrument);
      return status === 'outdated';
    });
  }, [instruments]);

  const outdatedCount = outdatedInstruments.length;

  // Check if there are any archived instruments (for showing/hiding toggle)
  const hasArchivedItems = useMemo(() =>
    instruments.some(inst => inst.status === 'archived'),
    [instruments]
  );

  // Selection management
  const selectedInstrumentsList = useMemo(() =>
    instruments.filter(inst => selectedInstruments.has(inst.id)),
    [instruments, selectedInstruments]
  );

  // Selection status for conditional Archive/Restore button
  const isSelectionAllArchived = useMemo(() =>
    selectedInstrumentsList.length > 0 && selectedInstrumentsList.every(inst => inst.status === 'archived'),
    [selectedInstrumentsList]
  );
  const isSelectionAllActive = useMemo(() =>
    selectedInstrumentsList.length > 0 && selectedInstrumentsList.every(inst => inst.status !== 'archived'),
    [selectedInstrumentsList]
  );
  const isSelectionMixed = selectedInstrumentsList.length > 0 && !isSelectionAllArchived && !isSelectionAllActive;

  const clearSelection = (): void => {
    setSelectedInstruments(new Set());
    if (gridApi) {
      gridApi.deselectAll();
    }
  };

  // Bulk action handlers
  const handleBulkEdit = (): void => {
    if (selectedInstruments.size === 1) {
      const instrument = selectedInstrumentsList[0];
      setEditingInstrument(instrument);
      onModalOpen();
    }
  };

  const handleBulkDelete = async (): Promise<void> => {
    if (selectedInstruments.size === 0) return;
    
    try {
      setIsLoadingUsage(true);
      
      // Scan usage for all selected instruments
      const instrumentIds = selectedInstrumentsList.map(inst => inst.id);
      const usageResults = await instrumentUsageService.scanBulkInstrumentUsage(instrumentIds);
      
      // Set usage results and open modal
      setDeleteCandidates(usageResults.scannedInstruments);
      onDeleteModalOpen();
      
    } catch (error) {
      console.error('Failed to scan instrument usage:', error);
      // TODO: Add toast notification for error feedback
    } finally {
      setIsLoadingUsage(false);
    }
  };

  // Handle delete confirmation from modal
  const handleDeleteConfirm = async (instruments: InstrumentUsageResult[]): Promise<void> => {
    // Mark that an action is being performed (for auto-return logic)
    justPerformedActionRef.current = true;

    try {
      setIsProcessingDelete(true);

      // Execute smart delete operation via InstrumentService
      const result = await instrumentService.executeSmartDelete(instruments);
      
      console.log('Delete operation completed:', {
        archived: result.archive.successIds.length,
        deleted: result.softDelete.successIds.length,
        total: result.total
      });
      
      // Clear selection after successful delete
      clearSelection();
      
      // Refresh instruments list to reflect changes
      handleInstrumentsUpdated();
      
    } catch (error) {
      console.error('Failed to delete instruments:', error);
      // TODO: Add toast notification for error feedback
    } finally {
      setIsProcessingDelete(false);
    }
  };

  const handleBulkArchive = async (): Promise<void> => {
    if (selectedInstruments.size === 0) return;

    // Mark that an action is being performed (for auto-return logic)
    justPerformedActionRef.current = true;

    try {
      const instrumentIds = selectedInstrumentsList.map(inst => inst.id);
      const result = await instrumentService.archiveInstruments(instrumentIds);

      // Show appropriate toast based on results
      if (result.successIds.length > 0 && result.failedIds.length === 0) {
        // All succeeded
        addToast({
          title: 'Instruments Archived',
          description: `${result.successIds.length} instrument${result.successIds.length > 1 ? 's' : ''} archived successfully`,
          color: 'success',
          timeout: 3000
        });
      } else if (result.successIds.length > 0 && result.failedIds.length > 0) {
        // Partial success
        addToast({
          title: 'Partial Archive',
          description: `${result.successIds.length} archived, ${result.failedIds.length} failed`,
          color: 'warning',
          timeout: 5000
        });
      } else {
        // All failed
        addToast({
          title: 'Archive Failed',
          description: `Failed to archive ${result.failedIds.length} instrument${result.failedIds.length > 1 ? 's' : ''}`,
          color: 'danger',
          timeout: 5000
        });
      }

      // Clear selection after archive attempt
      clearSelection();

      // Refresh instruments list to reflect changes
      handleInstrumentsUpdated();

    } catch (error) {
      console.error('Failed to archive instruments:', error);
      addToast({
        title: 'Archive Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        color: 'danger',
        timeout: 5000
      });
    }
  };

  const handleBulkRestore = async (): Promise<void> => {
    if (selectedInstruments.size === 0) return;

    // Mark that an action is being performed (for auto-return logic)
    justPerformedActionRef.current = true;

    try {
      const instrumentIds = selectedInstrumentsList.map(inst => inst.id);
      const result = await instrumentService.restoreInstruments(instrumentIds);

      // Show appropriate toast based on results
      if (result.successIds.length > 0 && result.failedIds.length === 0) {
        // All succeeded
        addToast({
          title: 'Instruments Restored',
          description: `${result.successIds.length} instrument${result.successIds.length > 1 ? 's' : ''} restored successfully`,
          color: 'success',
          timeout: 3000
        });
      } else if (result.successIds.length > 0 && result.failedIds.length > 0) {
        // Partial success
        addToast({
          title: 'Partial Restore',
          description: `${result.successIds.length} restored, ${result.failedIds.length} failed`,
          color: 'warning',
          timeout: 5000
        });
      } else {
        // All failed
        addToast({
          title: 'Restore Failed',
          description: `Failed to restore ${result.failedIds.length} instrument${result.failedIds.length > 1 ? 's' : ''}`,
          color: 'danger',
          timeout: 5000
        });
      }

      // Clear selection after restore attempt
      clearSelection();

      // Refresh instruments list to reflect changes
      handleInstrumentsUpdated();

    } catch (error) {
      console.error('Failed to restore instruments:', error);
      addToast({
        title: 'Restore Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        color: 'danger',
        timeout: 5000
      });
    }
  };

  // Single instrument handlers (for mobile action sheet and desktop toolbar)
  const handleSingleView = (instrument: Instrument): void => {
    setViewingInstrument(instrument);
    onModalOpen();
    onActionSheetClose();
  };

  const handleSingleEdit = (instrument: Instrument): void => {
    setEditingInstrument(instrument);
    onModalOpen();
    onActionSheetClose();
  };

  const handleSingleDuplicate = (instrument: Instrument): void => {
    setDuplicatingInstrument(instrument);
    onModalOpen();
    onActionSheetClose();
  };

  const handleSingleArchive = async (instrument: Instrument): Promise<void> => {
    // Mark that an action is being performed (for auto-return logic)
    justPerformedActionRef.current = true;

    try {
      const result = await instrumentService.archiveInstruments([instrument.id]);

      if (result.successIds.includes(instrument.id)) {
        addToast({
          title: 'Instrument Archived',
          description: `${instrument.symbol} has been archived successfully`,
          color: 'success',
          timeout: 3000
        });
        handleInstrumentsUpdated();
      } else {
        const errorMessage = result.errors?.[instrument.id] || 'Unknown error occurred';
        addToast({
          title: 'Archive Failed',
          description: errorMessage,
          color: 'danger',
          timeout: 5000
        });
      }
    } catch (error) {
      console.error('Failed to archive instrument:', error);
      addToast({
        title: 'Archive Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        color: 'danger',
        timeout: 5000
      });
    } finally {
      onActionSheetClose();
      clearSelection();
    }
  };

  const handleSingleRestore = async (instrument: Instrument): Promise<void> => {
    // Mark that an action is being performed (for auto-return logic)
    justPerformedActionRef.current = true;

    try {
      const result = await instrumentService.restoreInstruments([instrument.id]);

      if (result.successIds.includes(instrument.id)) {
        addToast({
          title: 'Instrument Restored',
          description: `${instrument.symbol} has been restored successfully`,
          color: 'success',
          timeout: 3000
        });
        handleInstrumentsUpdated();
      } else {
        const errorMessage = result.errors?.[instrument.id] || 'Unknown error occurred';
        addToast({
          title: 'Restore Failed',
          description: errorMessage,
          color: 'danger',
          timeout: 5000
        });
      }
    } catch (error) {
      console.error('Failed to restore instrument:', error);
      addToast({
        title: 'Restore Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        color: 'danger',
        timeout: 5000
      });
    } finally {
      onActionSheetClose();
      clearSelection();
    }
  };

  const handleSingleDelete = async (instrument: Instrument): Promise<void> => {
    try {
      setIsLoadingUsage(true);
      const usageResults = await instrumentUsageService.scanBulkInstrumentUsage([instrument.id]);
      setDeleteCandidates(usageResults.scannedInstruments);
      onDeleteModalOpen();
    } catch (error) {
      console.error('Failed to scan instrument usage:', error);
      addToast({
        title: 'Error',
        description: 'Failed to check instrument usage',
        color: 'danger',
        timeout: 5000
      });
    } finally {
      setIsLoadingUsage(false);
      onActionSheetClose();
      // Note: Don't clear selection yet - wait until delete completes
    }
  };

  const handleActionSheetClose = (): void => {
    onActionSheetClose();
    clearSelection();
  };

  // Modal handlers
  const handleModalSave = (instrument: Instrument): void => {
    const isUpdate = !!editingInstrument;

    // Update or add instrument to the list
    if (isUpdate) {
      // Update existing instrument
      setInstruments(prev => prev.map(inst =>
        inst.id === instrument.id ? instrument : inst
      ));
    } else {
      // Add new instrument
      setInstruments(prev => [...prev, instrument]);
    }

    // Clear editing state
    setEditingInstrument(undefined);

    // Show success toast
    addToast({
      title: isUpdate ? 'Instrument Updated' : 'Instrument Created',
      description: `${instrument.symbol} ${isUpdate ? 'updated' : 'created'} successfully`,
      color: 'success',
      timeout: 3000
    });
  };

  const handleModalError = (error: string): void => {
    console.error('Modal error:', error);
    // TODO: Could add toast notification here
  };

  const handleModalClose = (): void => {
    setEditingInstrument(undefined);
    setViewingInstrument(undefined);
    setDuplicatingInstrument(undefined);
    clearSelection(); // Clear selection when modal closes
    onModalClose();
  };

  // Load instruments from database
  const loadInstruments = async (): Promise<void> => {
    try {
      setLoading(true);
      const userInstruments = await instrumentService.getAll();
      setInstruments(userInstruments);
    } catch (error) {
      console.error('Failed to load instruments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstruments();
  }, []); // loadInstruments is intentionally not included as it's a stable function

  // Clear selection when switching between desktop/mobile to prevent stale multi-select state
  useEffect(() => {
    setSelectedInstruments(new Set());
    gridApi?.deselectAll();
  }, [isDesktop, gridApi]);

  // Get unique filter options from data
  const uniqueAssetClasses = useMemo(() => {
    const classes = [...new Set(instruments.map(i => i.classification.assetClass))].sort();
    return ['all', ...classes];
  }, [instruments]);

  // Get unique market segments and sub asset classes for filters - filtered by selected asset class
  const availableMarketSegments = useMemo(() => {
    const filteredData = state.assetClassFilter.has('all') 
      ? instruments 
      : instruments.filter(item => state.assetClassFilter.has(item.classification.assetClass));
    return Array.from(new Set(filteredData.map(item => item.classification?.marketSegment).filter(Boolean))).sort();
  }, [instruments, state.assetClassFilter]);

  const availableSubAssetClasses = useMemo(() => {
    const filteredData = state.assetClassFilter.has('all') 
      ? instruments 
      : instruments.filter(item => state.assetClassFilter.has(item.classification.assetClass));
    return Array.from(new Set(filteredData.map(item => item.classification?.subAssetClass).filter(Boolean))).sort();
  }, [instruments, state.assetClassFilter]);

  // Filter instruments based on search and filters
  const filteredInstruments = useMemo(() => {
    return instruments.filter(instrument => {
      // Apply archive visibility filter first (separate from preset system)
      // Only show archived items when showArchived is true or when in 'view-archive' preset
      if (!showArchived && state.selectedPreset !== 'view-archive' && instrument.status === 'archived') {
        return false;
      }

      // Apply classification-based filters using shared utility
      const passesClassificationFilters = applyClassificationFilters(instrument, {
        searchTerm: state.searchTerm,
        assetClassFilter: state.assetClassFilter,
        marketSegmentFilter: state.marketSegmentFilter,
        subAssetClassFilter: state.subAssetClassFilter
      });

      // Apply instrument-specific filters
      // Note: Allow archived items to bypass statusFilter when showArchived toggle is on
      // or when in 'view-archive' preset (since those already passed the visibility filter above)
      const matchesStatus = state.statusFilter.has('all') ||
        (state.statusFilter.has('active') && instrument.status === 'active') ||
        (state.statusFilter.has('inactive') && instrument.status === 'inactive') ||
        (state.statusFilter.has('archived') && instrument.status === 'archived') ||
        ((showArchived || state.selectedPreset === 'view-archive') && instrument.status === 'archived');

      const templateStatus = instrumentService.getTemplateStatus(instrument);
      const matchesTemplateStatus = state.templateStatusFilter.has('all') ||
        (state.templateStatusFilter.has('up-to-date') && templateStatus === 'up-to-date') ||
        (state.templateStatusFilter.has('outdated') && templateStatus === 'outdated') ||
        (state.templateStatusFilter.has('custom') && templateStatus === 'custom');

      const matchesSource = state.sourceFilter.has('all') ||
        (state.sourceFilter.has('catalog') && !!instrument.instrumentReferenceId) ||
        (state.sourceFilter.has('custom') && !instrument.instrumentReferenceId);

      return passesClassificationFilters && matchesStatus && matchesTemplateStatus && matchesSource;
    });
  }, [instruments, state.searchTerm, state.assetClassFilter, state.marketSegmentFilter, state.subAssetClassFilter, state.statusFilter, state.templateStatusFilter, state.sourceFilter, showArchived, state.selectedPreset]);

  // Auto-return to default view when filtered view empties after an action
  useEffect(() => {
    if (filteredInstruments.length === 0 && justPerformedActionRef.current) {
      // Reset to general preset and hide archived
      const generalColumns = new Set(['symbol', 'displayName', 'assetClass', 'marketSegment', 'subAssetClass', 'status', 'quoteCurrency']);
      setState(prev => ({
        ...prev,
        visibleColumns: generalColumns,
        selectedPreset: 'general',
        statusFilter: new Set(['active', 'inactive']),
        sourceFilter: new Set(['all']),
        templateStatusFilter: new Set(['all'])
      }));
      setShowArchived(false);
    }
    // Always reset the ref to prevent stale state
    justPerformedActionRef.current = false;
  }, [filteredInstruments.length]);

  // AG Grid column definitions with visibility controls
  const columnDefs: ColDef<Instrument>[] = useMemo(() => [
    {
      headerName: 'Symbol',
      field: 'symbol',
      width: 100,
      pinned: 'left',
      cellStyle: { fontWeight: 'bold' },
      hide: !state.visibleColumns.has('symbol')
    },
    {
      headerName: 'Name',
      field: 'displayName',
      flex: 1,
      minWidth: 200,
      hide: !state.visibleColumns.has('displayName')
    },
    {
      headerName: 'Asset Class',
      field: 'classification.assetClass',
      width: 120,
      valueGetter: (params) => params.data?.classification?.assetClass || '',
      valueFormatter: (params) => capitalizeText(params.value || ''),
      hide: !state.visibleColumns.has('assetClass')
    },
    {
      headerName: 'Market Segment',
      field: 'classification.marketSegment',
      width: 130,
      valueGetter: (params) => params.data?.classification?.marketSegment || '',
      valueFormatter: (params) => capitalizeText(params.value || ''),
      hide: !state.visibleColumns.has('marketSegment')
    },
    {
      headerName: 'Sub-Asset Class',
      field: 'classification.subAssetClass',
      width: 130,
      valueGetter: (params) => params.data?.classification?.subAssetClass || '',
      valueFormatter: (params) => capitalizeText(params.value || ''),
      hide: !state.visibleColumns.has('subAssetClass')
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 100,
      cellRenderer: (params: { data?: Instrument }) => params.data ? <StatusRenderer data={params.data} /> : '',
      hide: !state.visibleColumns.has('status')
    },
    {
      headerName: 'Source',
      width: 100,
      valueGetter: (params) => {
        const instrument = params.data;
        return instrument?.instrumentReferenceId ? 'Catalog' : 'Custom';
      },
      sortable: true,
      hide: !state.visibleColumns.has('source')
    },
    {
      headerName: 'Contract Size',
      field: 'contractSize',
      width: 130,
      valueGetter: (params) => {
        const data = params.data;
        if (data?.contractSize && data?.contractAsset) {
          return `${data.contractSize.toLocaleString()} ${data.contractAsset}`;
        }
        return '-';
      },
      hide: !state.visibleColumns.has('contractSize')
    },
    {
      headerName: 'Increment',
      field: 'incrementSize',
      width: 110,
      valueGetter: (params) => {
        const data = params.data;
        if (data?.incrementSize && data?.incrementType && data?.quoteCurrency) {
          return instrumentService.formatIncrementSize(data);
        }
        return '-';
      },
      hide: !state.visibleColumns.has('incrementSize')
    },
    {
      headerName: 'Contract Value per Increment',
      width: 110,
      valueGetter: (params) => {
        const data = params.data;
        if (data?.contractSize && data?.incrementSize && data?.incrementType && data?.quoteCurrency && data?.pricePrecision !== undefined) {
          return instrumentService.formatTickValue(data);
        }
        return '-';
      },
      hide: !state.visibleColumns.has('tickValue')
    },
    {
      headerName: 'Price Precision',
      field: 'pricePrecision',
      width: 120,
      hide: !state.visibleColumns.has('pricePrecision')
    },
    {
      headerName: 'Quote Currency',
      field: 'quoteCurrency',
      width: 120,
      hide: !state.visibleColumns.has('quoteCurrency')
    },
    {
      headerName: 'Reference ID',
      field: 'instrumentReferenceId',
      width: 120,
      hide: !state.visibleColumns.has('instrumentReferenceId'),
      valueGetter: (params) => params.data?.instrumentReferenceId || '-'
    },
    {
      headerName: 'Template Status',
      field: 'templateStatus',
      width: 140,
      cellRenderer: (params: { data?: Instrument }) => {
        if (!params.data) return '';

        const status = instrumentService.getTemplateStatus(params.data);

        const statusConfig = {
          'up-to-date': { color: 'success' as const, text: 'Up-to-date' },
          'outdated': { color: 'warning' as const, text: 'Outdated' },
          'custom': { color: 'primary' as const, text: 'Custom' }
        };

        const config = statusConfig[status];
        return <Chip color={config.color} size="sm" variant="flat">{config.text}</Chip>;
      },
      hide: !state.visibleColumns.has('templateStatus')
    },
    {
      headerName: 'Created At',
      field: 'createdAt',
      width: 120,
      valueFormatter: (params) => formatDate(new Date(params.value)),
      hide: !state.visibleColumns.has('createdAt')
    },
    {
      headerName: 'Updated At',
      field: 'updatedAt',
      width: 120,
      valueFormatter: (params) => formatDate(new Date(params.value)),
      hide: !state.visibleColumns.has('updatedAt')
    }
  ], [state.visibleColumns]);

  // Mobile-specific column definitions (Symbol + Name only, ignores state.visibleColumns)
  const mobileColumnDefs: ColDef<Instrument>[] = useMemo(() => [
    {
      headerName: 'Symbol',
      field: 'symbol',
      width: 100,
      pinned: 'left',
      cellStyle: { fontWeight: 'bold' }
    },
    {
      headerName: 'Name',
      field: 'displayName',
      flex: 1,
      minWidth: 150,
      // Add "(Archived)" suffix for archived instruments on mobile
      valueFormatter: (params) => {
        const name = params.value || '';
        return params.data?.status === 'archived' ? `${name} (Archived)` : name;
      }
    }
  ], []);

  // Preset options for ButtonGroup
  const presetOptions = useMemo(() => getPresetOptions().map(preset => ({
    value: preset.key,
    label: preset.label
  })), []);

  const handlePresetChange = (value: string | number | (string | number)[] | null): void => {
    if (typeof value === 'string') {
      const presetKey = value as PresetKey;
      const result = applyPreset(presetKey, state.visibleColumns);
      
      // Update the column visibility
      const newVisibleColumns = new Set(result.visibleColumns);
      
      // Update state with new columns and preset
      setState({
        ...state,
        visibleColumns: newVisibleColumns,
        selectedPreset: result.selectedPreset,
        // Apply preset-specific filtering
        ...(result.shouldFilter && presetKey === 'template-review' && {
          sourceFilter: new Set(['catalog']),
          statusFilter: new Set(['active', 'inactive']) // Reset to default (exclude archived)
        }),
        ...(result.shouldFilter && presetKey === 'view-archive' && {
          statusFilter: new Set(['archived'])
        }),
        // Reset status filter for other presets (exclude archived by default)
        ...(presetKey !== 'view-archive' && presetKey !== 'template-review' && {
          statusFilter: new Set(['active', 'inactive'])
        }),
        // Close drawer on mobile after preset selection
        isDrawerOpen: isDesktop ? state.isDrawerOpen : false
      });
    }
  };

  const toggleColumnVisibility = (columnId: string): void => {
    setState(prev => {
      const newSet = new Set(prev.visibleColumns);
      if (newSet.has(columnId)) {
        newSet.delete(columnId);
      } else {
        newSet.add(columnId);
      }
      
      // Detect if the new column set matches any preset, otherwise switch to custom
      const detectedPreset = detectCurrentPreset(newSet);
      
      return { 
        ...prev, 
        visibleColumns: newSet,
        selectedPreset: detectedPreset
      };
    });
  };

  const clearFilters = (): void => {
    setState(prev => ({
      ...prev,
      searchTerm: '',
      assetClassFilter: new Set(['all']),
      marketSegmentFilter: new Set(['all']),
      subAssetClassFilter: new Set(['all']),
      statusFilter: new Set(['all']),
      sourceFilter: new Set(['all'])
    }));
  };

  const handleInstrumentsUpdated = (): void => {
    loadInstruments(); // Refresh local instruments data
  };

  // Popover action handlers
  const handleAddPopoverCreateNew = (): void => {
    setIsAddPopoverOpen(false);
    setEditingInstrument(undefined);
    onModalOpen();
  };

  const handleAddPopoverImport = (): void => {
    setIsAddPopoverOpen(false);
    onImportModalOpen();
  };

  const handleImportComplete = (_importedIds: string[]): void => {
    // Toast is already shown by InstrumentImportModal - just refresh the list
    handleInstrumentsUpdated();
  };

  if (loading) {
    return (
      <Card shadow="none">
        <CardBody className="p-0">
          <div className="text-center py-8">Loading instruments...</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card shadow="none" radius="none" className="flex flex-col h-full">
      {/* Toolbar Header */}
      <CardHeader className="p-0 shrink-0 pb-2">
        {/* Mobile Toolbar */}
        {isMobile && (
          <div className="flex flex-col gap-2 w-full">
            {/* Row 1: Search only */}
            <Input
              placeholder="Search..."
              aria-label="Search instruments"
              value={state.searchTerm}
              onChange={(e) => setState(prev => ({ ...prev, searchTerm: e.target.value }))}
              startContent={<Search size={18} />}
              isClearable
              onClear={() => setState(prev => ({ ...prev, searchTerm: '' }))}
              size="lg"
              className="w-full [&_input]:text-base"
            />

            {/* Row 2: Archive Toggle + Filter + Add */}
            <div className="flex items-center gap-2">

              {/* Filter Button */}
              <div className="relative">
                <Button
                  size="sm"
                  variant="light"
                  isIconOnly
                  aria-label="Filter instruments"
                  onPress={() => setState(prev => ({ ...prev, isDrawerOpen: true }))}
                >
                  <Filter size={16} />
                </Button>
                {/* Badge for outdated instruments */}
                {outdatedCount > 0 && (
                  <span className="absolute top-0 right-0 w-2 h-2 bg-warning rounded-full" />
                )}
              </div>

              {/* Archive Toggle - only show if there are archived items */}
              {hasArchivedItems && (
                <Button
                  size="sm"
                  variant="light"
                  startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                  onPress={() => setShowArchived(!showArchived)}
                  className={showArchived ? 'text-text-primary' : 'text-text-tertiary'}
                >
                  Archived
                </Button>
              )}

              {/* Spacer to push Add to the right */}
              <div className="flex-1" />

              {/* Add Button with Popover */}
              <Popover
                isOpen={isAddPopoverOpen}
                onOpenChange={setIsAddPopoverOpen}
                placement="bottom-end"
              >
                <PopoverTrigger>
                  <Button
                    size="sm"
                    variant="light"
                    startContent={<Plus size={16} />}
                  >
                    New
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-1">
                  <div className="flex flex-col min-w-40">
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start"
                      startContent={<Plus size={16} />}
                      onPress={handleAddPopoverCreateNew}
                    >
                      Create New...
                    </Button>
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start"
                      startContent={<Package size={16} />}
                      onPress={handleAddPopoverImport}
                    >
                      Import from Catalog...
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}

        {/* Desktop Toolbar */}
        {isDesktop && (
          <div className="flex flex-row justify-between items-center gap-3 w-full">
            {/* Left side - View controls and Filter */}
            <div className="flex items-center gap-4">

              {/* View Preset Selection */}
              <Card shadow="none" radius="sm" className="border-1 border-default-200 p-0 m-0">
                <div className="flex items-center gap-3 ">
                  <span className="text-xs text-text-primary pl-2 pr-2 border-r-1 border-default-200">Preset Filters</span>
                  <ButtonGroup
                    options={presetOptions}
                    value={state.selectedPreset}
                    onChange={handlePresetChange}
                    variant="light"
                    size="sm"
                    radius="none"
                    className="shrink-0"
                    selectedColor='secondary'
                    unselectedColor='default'
                  />
                </div>
              </Card>

              {/* Filter Button */}
              <Button
                size="sm"
                variant="light"
                startContent={<Filter size={14} />}
                onPress={() => setState(prev => ({ ...prev, isDrawerOpen: true }))}
              >
                Custom Filters
              </Button>

              {/* Archive Toggle - only show if there are archived items */}
              {hasArchivedItems && (
                <Button
                  size="sm"
                  variant="light"
                  startContent={showArchived ? <Eye size={14} /> : <EyeOff size={14} />}
                  onPress={() => setShowArchived(!showArchived)}
                  className={showArchived ? 'text-text-primary' : 'text-text-tertiary'}
                >
                  Archived
                </Button>
              )}
            </div>

            {/* Right side - Add button only (selection actions moved to CardFooter) */}
            <div className="flex items-center gap-2">
              {/* Add Button with Popover */}
              <Popover
                isOpen={isAddPopoverOpen}
                onOpenChange={setIsAddPopoverOpen}
                placement="bottom-end"
              >
                <PopoverTrigger>
                  <Button
                    size="sm"
                    variant="light"
                    startContent={<Plus size={14} />}
                  >
                    Add Instrument
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-1">
                  <div className="flex flex-col min-w-40">
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start"
                      startContent={<Plus size={16} />}
                      onPress={handleAddPopoverCreateNew}
                    >
                      Create New...
                    </Button>
                    <Button
                      size="md"
                      variant="light"
                      className="justify-start"
                      startContent={<Package size={16} />}
                      onPress={handleAddPopoverImport}
                    >
                      Import from Catalog...
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}
      </CardHeader>

      {/* AG Grid Table */}
      <CardBody className="p-0 flex-1 min-h-0">
        <AGGridTable<Instrument>
            rowData={filteredInstruments}
            columnDefs={isDesktop ? columnDefs : mobileColumnDefs}
            height="100%"
            loading={loading}
            defaultColDef={{
              resizable: isDesktop,
              suppressMovable: isMobile,
              sortable: true,
              filter: isDesktop,
              minWidth: isMobile ? 80 : 100
            }}
            suppressMenuHide={isMobile}
            suppressDragLeaveHidesColumns={true}
            rowSelection={isDesktop
              ? { mode: 'multiRow', checkboxes: true, headerCheckbox: true }
              : { mode: 'singleRow', enableClickSelection: true, checkboxes: false }
            }
            gridOptions={{
              onGridReady: (params) => {
                setGridApi(params.api);
              },
              onSelectionChanged: (event) => {
                const selectedRows = event.api.getSelectedRows();
                const selectedIds = new Set(selectedRows.map((row: Instrument) => row.id));

                // Guard against spurious updates during re-renders
                const previousCount = selectedInstruments.size;
                const newCount = selectedIds.size;

                setSelectedInstruments(selectedIds);

                // On mobile, open action sheet when exactly 1 row is selected
                if (isMobile && newCount === 1 && previousCount !== 1) {
                  onActionSheetOpen();
                } else if (isMobile && newCount === 0 && previousCount > 0) {
                  onActionSheetClose();
                }
              },
              // Improve mobile touch targets
              rowHeight: 48,
              // Apply muted styling to archived rows
              rowClassRules: {
                'instrument-row-archived': (params) => params.data?.status === 'archived'
              }
            }}
          />
      </CardBody>

      {/* Desktop Selection Actions Footer - Only visible when items selected */}
      {isDesktop && selectedInstruments.size > 0 && (
        <CardFooter className="py-2 px-4">
          <div className="flex items-center justify-between w-full">
            {/* Selection Info */}
            <div className="flex items-center gap-2">
              <Chip size="sm" color="primary" variant="flat">
                {selectedInstruments.size} selected
              </Chip>
              <Button
                size="sm"
                variant="light"
                onPress={clearSelection}
                className="min-w-0 px-2"
              >
                Clear
              </Button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* View Button - Only enabled when exactly 1 item selected */}
              <Button
                size="sm"
                variant="bordered"
                startContent={<Eye size={14} />}
                isDisabled={selectedInstruments.size !== 1}
                onPress={() => selectedInstrumentsList[0] && handleSingleView(selectedInstrumentsList[0])}
                title={
                  selectedInstruments.size > 1
                    ? "Select only one instrument to view"
                    : "View selected instrument"
                }
                className="min-w-0"
              >
                View
              </Button>

              {/* Edit Button - Only enabled when exactly 1 item selected */}
              <Button
                size="sm"
                variant="bordered"
                startContent={<Edit size={14} />}
                isDisabled={selectedInstruments.size !== 1}
                onClick={handleBulkEdit}
                title={
                  selectedInstruments.size > 1
                    ? "Select only one instrument to edit"
                    : "Edit selected instrument"
                }
                className="min-w-0"
              >
                Edit
              </Button>

              {/* Duplicate Button - Only enabled when exactly 1 item selected */}
              <Button
                size="sm"
                variant="bordered"
                startContent={<Copy size={14} />}
                isDisabled={selectedInstruments.size !== 1}
                onPress={() => selectedInstrumentsList[0] && handleSingleDuplicate(selectedInstrumentsList[0])}
                title={
                  selectedInstruments.size > 1
                    ? "Select only one instrument to duplicate"
                    : "Duplicate selected instrument"
                }
                className="min-w-0"
              >
                Duplicate
              </Button>

              {/* Archive/Restore Button - Conditional based on selection status */}
              {isSelectionAllArchived ? (
                <Button
                  size="sm"
                  variant="bordered"
                  startContent={<RotateCcw size={14} />}
                  onPress={handleBulkRestore}
                  title={`Restore ${selectedInstruments.size} instrument${selectedInstruments.size > 1 ? 's' : ''}`}
                  className="min-w-0"
                >
                  Restore
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="bordered"
                  startContent={<Archive size={14} />}
                  isDisabled={isSelectionMixed}
                  onPress={handleBulkArchive}
                  title={
                    isSelectionMixed
                      ? "Select only archived or only active instruments"
                      : `Archive ${selectedInstruments.size} instrument${selectedInstruments.size > 1 ? 's' : ''}`
                  }
                  className="min-w-0"
                >
                  Archive
                </Button>
              )}

              {/* Delete Button */}
              <Button
                size="sm"
                color="danger"
                variant="bordered"
                startContent={<Trash2 size={14} />}
                onPress={handleBulkDelete}
                title={`Delete ${selectedInstruments.size} instrument${selectedInstruments.size > 1 ? 's' : ''}`}
                className="min-w-0"
              >
                Delete
              </Button>
            </div>
          </div>
        </CardFooter>
      )}

        {/* Filter/Search Drawer */}
        <Drawer 
          isOpen={state.isDrawerOpen} 
          onOpenChange={(open) => setState(prev => ({ ...prev, isDrawerOpen: open }))}
          placement="right"
          size="sm"
        >
          <DrawerContent>
            <DrawerHeader className="flex flex-col gap-1">
              <h2 className="text-lg font-semibold">Search & Filters</h2>
              <p className="text-small text-foreground-500">Filter your instrument library</p>
            </DrawerHeader>
            <DrawerBody>
              <div className="space-y-6">
                {/* Search Input - Desktop only (mobile has inline search in toolbar) */}
                {isDesktop && (
                  <div>
                    <Input
                      label="Search Instruments"
                      placeholder="Search by symbol or name..."
                      value={state.searchTerm}
                      onChange={(e) => setState(prev => ({ ...prev, searchTerm: e.target.value }))}
                      startContent={<Search size={18} />}
                      isClearable
                      onClear={() => setState(prev => ({ ...prev, searchTerm: '' }))}
                      className="[&_input]:text-base"
                    />
                  </div>
                )}

                {/* Asset Class Filter */}
                <div>
                  <Select
                    label="Asset Class"
                    aria-label="Asset Class"
                    placeholder="Select asset classes"
                    selectionMode="multiple"
                    selectedKeys={state.assetClassFilter}
                    onSelectionChange={(keys) => {
                      const newSelection = handleFilterSelection(new Set(Array.from(keys) as string[]), state.assetClassFilter);
                      setState(prev => ({ ...prev, assetClassFilter: newSelection }));
                    }}
                  >
                    <SelectItem key="all">All Classes</SelectItem>
                    <>
                      {uniqueAssetClasses.filter(ac => ac !== 'all').map(assetClass => (
                        <SelectItem key={assetClass}>{capitalizeText(assetClass)}</SelectItem>
                      ))}
                    </>
                  </Select>
                </div>

                {/* Market Segment Filter */}
                <div>
                  <Select
                    label="Market Segment"
                    aria-label="Market Segment"
                    placeholder="Select market segments"
                    selectionMode="multiple"
                    selectedKeys={state.marketSegmentFilter}
                    onSelectionChange={(keys) => {
                      const newSelection = handleFilterSelection(new Set(Array.from(keys) as string[]), state.marketSegmentFilter);
                      setState(prev => ({ ...prev, marketSegmentFilter: newSelection }));
                    }}
                  >
                    <SelectItem key="all">All Segments</SelectItem>
                    <>
                      {availableMarketSegments.map(segment => 
                        <SelectItem key={segment}>{capitalizeText(segment || '')}</SelectItem>
                      )}
                    </>
                  </Select>
                </div>

                {/* Sub Asset Class Filter */}
                <div>
                  <Select
                    label="Sub Asset Class"
                    aria-label="Sub Asset Class"
                    placeholder="Select sub asset classes"
                    selectionMode="multiple"
                    selectedKeys={state.subAssetClassFilter}
                    onSelectionChange={(keys) => {
                      const newSelection = handleFilterSelection(new Set(Array.from(keys) as string[]), state.subAssetClassFilter);
                      setState(prev => ({ ...prev, subAssetClassFilter: newSelection }));
                    }}
                  >
                    <SelectItem key="all">All Sub Classes</SelectItem>
                    <>
                      {availableSubAssetClasses.map(subClass => 
                        <SelectItem key={subClass}>{capitalizeText(subClass || '')}</SelectItem>
                      )}
                    </>
                  </Select>
                </div>

                {/* Status Filter */}
                <div>
                  <Select
                    label="Status"
                    aria-label="Status"
                    placeholder="Select statuses"
                    selectionMode="multiple"
                    selectedKeys={state.statusFilter}
                    onSelectionChange={(keys) => {
                      const newSelection = handleFilterSelection(new Set(Array.from(keys) as string[]), state.statusFilter);
                      setState(prev => ({ ...prev, statusFilter: newSelection }));
                    }}
                  >
                    <SelectItem key="all">All Statuses</SelectItem>
                    <SelectItem key="active">Active</SelectItem>
                    <SelectItem key="inactive">Inactive</SelectItem>
                    <SelectItem key="archived">Archived</SelectItem>
                  </Select>
                </div>

                {/* Template Status Filter */}
                <div>
                  <Select
                    label="Template Status"
                    aria-label="Template Status"
                    placeholder="Select template statuses"
                    selectionMode="multiple"
                    selectedKeys={state.templateStatusFilter}
                    onSelectionChange={(keys) => {
                      const newSelection = handleFilterSelection(new Set(Array.from(keys) as string[]), state.templateStatusFilter);
                      setState(prev => ({ ...prev, templateStatusFilter: newSelection }));
                    }}
                  >
                    <SelectItem key="all">All Templates</SelectItem>
                    <SelectItem key="up-to-date">Up-to-date</SelectItem>
                    <SelectItem key="outdated">Outdated</SelectItem>
                    <SelectItem key="custom">Custom</SelectItem>
                  </Select>
                </div>

                {/* Source Filter */}
                <div>
                  <Select
                    label="Source"
                    aria-label="Source"
                    placeholder="Select sources"
                    selectionMode="multiple"
                    selectedKeys={state.sourceFilter}
                    onSelectionChange={(keys) => {
                      const newSelection = handleFilterSelection(new Set(Array.from(keys) as string[]), state.sourceFilter);
                      setState(prev => ({ ...prev, sourceFilter: newSelection }));
                    }}
                  >
                    <SelectItem key="all">All Sources</SelectItem>
                    <SelectItem key="catalog">From Catalog</SelectItem>
                    <SelectItem key="custom">Custom Added</SelectItem>
                  </Select>
                </div>


                {/* Column Visibility Controls - Desktop only (mobile has fixed 2-column layout) */}
                {isDesktop && (
                  <div className="border-t pt-6">
                    <h3 className="text-medium font-medium mb-3">Visible Columns</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'symbol', label: 'Symbol' },
                        { id: 'displayName', label: 'Name' },
                        { id: 'assetClass', label: 'Asset Class' },
                        { id: 'marketSegment', label: 'Market Segment' },
                        { id: 'subAssetClass', label: 'Sub Asset Class' },
                        { id: 'status', label: 'Status' },
                        { id: 'source', label: 'Source' },
                        { id: 'contractSize', label: 'Contract Size' },
                        { id: 'incrementSize', label: 'Min Move' },
                        { id: 'tickValue', label: 'Value/Move' },
                        { id: 'pricePrecision', label: 'Price Precision' },
                        { id: 'quoteCurrency', label: 'Quote Currency' },
                        { id: 'instrumentReferenceId', label: 'Reference ID' },
                        { id: 'templateStatus', label: 'Template Status' },
                        { id: 'createdAt', label: 'Created At' },
                        { id: 'updatedAt', label: 'Updated At' }
                      ].map((column) => (
                        <label key={column.id} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={state.visibleColumns.has(column.id)}
                            onChange={() => toggleColumnVisibility(column.id)}
                            className="rounded border-gray-300"
                          />
                          <span>{column.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}


                {/* Action Buttons */}
                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="flat" 
                    onPress={clearFilters}
                    className="flex-1"
                  >
                    Clear All
                  </Button>
                  <Button 
                    color="primary" 
                    onPress={() => setState(prev => ({ ...prev, isDrawerOpen: false }))}
                    className="flex-1"
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>
            </DrawerBody>
          </DrawerContent>
        </Drawer>

      {/* Instrument Modal */}
      <InstrumentModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        {...(editingInstrument && { instrument: editingInstrument })}
        {...(viewingInstrument && { instrument: viewingInstrument, mode: 'view' as const })}
        {...(duplicatingInstrument && { duplicateFrom: duplicatingInstrument })}
        onSave={handleModalSave}
        onError={handleModalError}
      />

      {/* Instrument Import Modal */}
      <InstrumentImportModal
        isOpen={isImportModalOpen}
        onClose={onImportModalClose}
        onImportComplete={handleImportComplete}
      />

      {/* Template Review Modal */}
      <TemplateReviewModal
        isOpen={isTemplateReviewModalOpen}
        onClose={onTemplateReviewModalClose}
        outdatedInstruments={outdatedInstruments}
        outdatedCount={outdatedCount}
        onInstrumentsUpdated={handleInstrumentsUpdated}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        instruments={deleteCandidates}
        onConfirm={handleDeleteConfirm}
        isLoadingUsage={isLoadingUsage}
        isProcessing={isProcessingDelete}
      />

      {/* Mobile Action Sheet */}
      <InstrumentActionSheet
        isOpen={isActionSheetOpen}
        onClose={handleActionSheetClose}
        instrument={selectedInstrumentsList[0] || null}
        onView={handleSingleView}
        onEdit={handleSingleEdit}
        onDuplicate={handleSingleDuplicate}
        onArchive={handleSingleArchive}
        onRestore={handleSingleRestore}
        onDelete={handleSingleDelete}
        isArchived={selectedInstrumentsList[0]?.status === 'archived'}
      />
    </Card>
  );
};